// S2 — detalle de los paros de un bloque, con las dos acciones del supervisor:
// CONFIRMAR (sella el paro como correcto) y CORREGIR (cambia causal y/u horas
// con razón obligatoria).
//
// Antes de esto, S2 mostraba "⚠ N paro(s)" y nada más: el supervisor era el
// dueño de la verdad del dato pero veía menos del paro que el técnico. Este
// diálogo cierra ese hueco.
//
// Los formatters de hora vienen de features/mantenimiento a propósito: los
// paros se guardan en fake-UTC (pared local etiquetada Z) y esa conversión
// tiene que ser LA MISMA en las dos pantallas, no una copia que se desincronice.

import { useState } from 'react';
import { toast } from 'sonner';
import {
  paroSupervisorCorregirSchema,
  type Paro,
  type ParoSupervisorCorregir,
  type ParoSupervisorDetalle,
} from 'schemas';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { Textarea } from '../../components/ui/textarea';
import {
  EstadoParoMttoBadge,
  fmtFechaHoraParo,
  fmtHoraParo,
  fmtMinutosParo,
  isoFromWall,
  minutosEntre,
} from '../mantenimiento/ParoDetalleDrawer';
import { esCodigoApi, mensajeError } from '../mantenimiento/shared';
import { useCausalesParo } from '../../lib/api/queries';
import { useConfirmarParoSupervisor, useCorregirParoSupervisor } from './parosSupervisorQueries';

export function ParosBloqueDialog({
  bloqueLabel,
  paros,
  detalle,
  onClose,
}: {
  bloqueLabel: string;
  paros: Paro[];
  detalle: Record<string, ParoSupervisorDetalle>;
  onClose: () => void;
}) {
  const [corrigiendoId, setCorrigiendoId] = useState<string | null>(null);
  const confirmar = useConfirmarParoSupervisor();

  const paroCorrigiendo = corrigiendoId
    ? (paros.find((p) => p.id === corrigiendoId) ?? null)
    : null;

  async function handleConfirmar(paroId: string) {
    try {
      await confirmar.mutateAsync({ paroId, input: { nota: null } });
      toast.success('Paro confirmado');
    } catch (err) {
      if (esCodigoApi(err, 'correccion_pendiente')) {
        toast.error(
          'Hay una corrección del técnico sin resolver. Resuélvala en “Correcciones de paros”.',
        );
        return;
      }
      if (esCodigoApi(err, 'already_confirmed')) {
        toast.error('Este paro ya estaba confirmado');
        return;
      }
      toast.error(mensajeError(err));
    }
  }

  if (paroCorrigiendo) {
    const d = detalle[paroCorrigiendo.id];
    return (
      <CorregirParoForm paro={paroCorrigiendo} detalle={d} onClose={() => setCorrigiendoId(null)} />
    );
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader>
        <DialogTitle>Paros del bloque {bloqueLabel}</DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        {paros.length === 0 ? (
          <p className="text-sm text-fg-tertiary">Este bloque no tiene paros.</p>
        ) : (
          paros.map((p) => {
            const d = detalle[p.id];
            return (
              <div key={p.id} className="rounded border border-border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono text-sm font-semibold">
                    {fmtHoraParo(p.hora_inicio)}–{p.hora_fin ? fmtHoraParo(p.hora_fin) : 'abierto'}
                    <span className="ml-2 font-sans text-xs font-normal text-fg-secondary">
                      {fmtMinutosParo(p.minutos_perdidos)}
                    </span>
                  </span>
                  {d ? <EstadoParoMttoBadge estado={d.estado_mtto} /> : null}
                </div>

                {d ? (
                  <>
                    <p className="mt-1.5 text-sm">
                      {d.causal_madre.nombre} → <strong>{d.causal.nombre}</strong>
                    </p>
                    <p className="text-xs text-fg-tertiary">
                      {d.categoria.nombre}
                      {d.categoria.es_mantenimiento ? ' · mantenimiento' : ''}
                      {d.categoria.afecta_disponibilidad ? '' : ' · no afecta disponibilidad'}
                    </p>

                    <dl className="mt-2 flex flex-col gap-0.5 text-xs text-fg-secondary">
                      {d.cerrado_por ? (
                        <div>
                          <dt className="inline font-medium">Cerrado por mantenimiento: </dt>
                          <dd className="inline">
                            {d.cerrado_por.nombre} {d.cerrado_por.apellido}
                          </dd>
                        </div>
                      ) : (
                        <div>
                          <dt className="inline font-medium">Cerrado por: </dt>
                          <dd className="inline">el líder</dd>
                        </div>
                      )}
                      {d.validado_mtto_at ? (
                        <div>
                          <dt className="inline font-medium">Revisado por mtto: </dt>
                          <dd className="inline">
                            {fmtFechaHoraParo(d.validado_mtto_at)}
                            {d.validado_mtto_por
                              ? ` · ${d.validado_mtto_por.nombre} ${d.validado_mtto_por.apellido}`
                              : ''}
                          </dd>
                        </div>
                      ) : null}
                      {d.confirmado_sup_at ? (
                        <div className="text-ok">
                          <dt className="inline font-medium">Confirmado: </dt>
                          <dd className="inline">
                            {fmtFechaHoraParo(d.confirmado_sup_at)}
                            {d.confirmado_sup_por
                              ? ` · ${d.confirmado_sup_por.nombre} ${d.confirmado_sup_por.apellido}`
                              : ''}
                          </dd>
                        </div>
                      ) : null}
                      {p.observaciones ? (
                        <div>
                          <dt className="inline font-medium">Observaciones: </dt>
                          <dd className="inline italic">{p.observaciones}</dd>
                        </div>
                      ) : null}
                    </dl>

                    {d.correccion_viva ? (
                      <p className="mt-2 rounded bg-warn-soft p-2 text-xs text-warn">
                        El técnico propuso una corrección: “{d.correccion_viva.razon}”. Resuélvala
                        en <strong>Correcciones de paros</strong> antes de confirmar.
                      </p>
                    ) : null}

                    <div className="mt-2 flex flex-wrap gap-2">
                      {d.puede_confirmar ? (
                        <Button
                          size="sm"
                          onClick={() => void handleConfirmar(p.id)}
                          disabled={confirmar.isPending}
                        >
                          {confirmar.isPending ? '…' : 'Confirmar'}
                        </Button>
                      ) : null}
                      {d.puede_corregir ? (
                        <Button size="sm" variant="outline" onClick={() => setCorrigiendoId(p.id)}>
                          Corregir
                        </Button>
                      ) : null}
                    </div>
                  </>
                ) : (
                  <p className="mt-1 text-xs text-fg-tertiary">
                    Sin detalle disponible para este paro.
                  </p>
                )}
              </div>
            );
          })
        )}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cerrar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Formulario de corrección del supervisor
// ---------------------------------------------------------------------------

/**
 * A diferencia de la corrección del técnico (que queda `propuesta` esperando
 * aprobación), esta se aplica en el acto: el supervisor ES la instancia que
 * aprueba. Por eso el texto del botón dice "Aplicar cambio", no "Proponer".
 */
function CorregirParoForm({
  paro,
  detalle,
  onClose,
}: {
  paro: Paro;
  detalle: ParoSupervisorDetalle | undefined;
  onClose: () => void;
}) {
  const corregir = useCorregirParoSupervisor();
  const causalesQ = useCausalesParo();
  const causales = causalesQ.data ?? [];

  const [causalMadreId, setCausalMadreId] = useState('');
  const [causalId, setCausalId] = useState('');
  const [horaInicioWall, setHoraInicioWall] = useState('');
  const [horaFinWall, setHoraFinWall] = useState('');
  const [razon, setRazon] = useState('');
  const [error, setError] = useState<string | null>(null);

  const madre = causales.find((m) => m.id === causalMadreId);
  const especificas = madre?.hijas ?? [];

  const cambiaCausal = causalMadreId !== '' && causalId !== '';
  const hayCambio = cambiaCausal || horaInicioWall !== '' || horaFinWall !== '';

  // Preview de minutos con los valores efectivos (propuesto ?? actual).
  const inicioEfectivo = horaInicioWall ? isoFromWall(horaInicioWall) : paro.hora_inicio;
  const finEfectivo = horaFinWall ? isoFromWall(horaFinWall) : paro.hora_fin;
  const minutosResultantes = finEfectivo ? minutosEntre(inicioEfectivo, finEfectivo) : null;

  async function handleAplicar() {
    setError(null);
    if (causalMadreId !== '' && causalId === '') {
      setError('Elija también la causal específica');
      return;
    }
    if (minutosResultantes !== null && minutosResultantes <= 0) {
      setError('La hora fin debe ser posterior a la hora inicio');
      return;
    }
    const input: ParoSupervisorCorregir = {
      ...(cambiaCausal ? { causal_madre_id: causalMadreId, causal_id: causalId } : {}),
      ...(horaInicioWall ? { hora_inicio: isoFromWall(horaInicioWall) } : {}),
      ...(horaFinWall ? { hora_fin: isoFromWall(horaFinWall) } : {}),
      razon: razon.trim(),
    };
    const parsed = paroSupervisorCorregirSchema.safeParse(input);
    if (!parsed.success) {
      const primero = parsed.error.errors[0];
      setError(primero ? primero.message : 'Datos inválidos');
      return;
    }
    try {
      const out = await corregir.mutateAsync({ paroId: paro.id, input: parsed.data });
      toast.success('Paro corregido');
      if (out.correccion_tecnico_superada) {
        toast.info('La corrección que había propuesto el técnico quedó superada; se le notificó.');
      }
      onClose();
    } catch (err) {
      if (esCodigoApi(err, 'cobertura_invalida')) {
        toast.error(
          'El cambio dejaría un bloque con producción 0 sin paros que lo cubran completo.',
        );
        return;
      }
      if (esCodigoApi(err, 'hora_fin_futura')) {
        toast.error('La hora no puede estar en el futuro');
        return;
      }
      toast.error(mensajeError(err));
    }
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader>
        <DialogTitle>Corregir paro</DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        <div className="rounded bg-surface-elevated p-2 text-xs text-fg-secondary">
          <p>
            Actual: <strong>{fmtHoraParo(paro.hora_inicio)}</strong>–
            <strong>{paro.hora_fin ? fmtHoraParo(paro.hora_fin) : '—'}</strong> ·{' '}
            {fmtMinutosParo(paro.minutos_perdidos)}
          </p>
          {detalle ? (
            <p>
              {detalle.causal_madre.nombre} → {detalle.causal.nombre}
            </p>
          ) : null}
        </div>

        {causalesQ.isLoading ? (
          <p className="flex items-center gap-2 text-xs text-fg-tertiary">
            <Spinner className="size-3.5" /> Cargando causales…
          </p>
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sup-corr-madre">Nueva categoría (opcional)</Label>
              <Select
                id="sup-corr-madre"
                value={causalMadreId}
                onChange={(e) => {
                  setCausalMadreId(e.target.value);
                  setCausalId('');
                }}
              >
                <option value="">— Sin cambio de causal —</option>
                {causales.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nombre}
                  </option>
                ))}
              </Select>
            </div>
            {causalMadreId !== '' ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="sup-corr-especifica">Nueva causal específica</Label>
                <Select
                  id="sup-corr-especifica"
                  value={causalId}
                  onChange={(e) => setCausalId(e.target.value)}
                >
                  <option value="">— Elija —</option>
                  {especificas.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
          </>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sup-corr-inicio">Nueva hora inicio (opcional)</Label>
            <Input
              id="sup-corr-inicio"
              type="datetime-local"
              value={horaInicioWall}
              onChange={(e) => setHoraInicioWall(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sup-corr-fin">Nueva hora fin (opcional)</Label>
            <Input
              id="sup-corr-fin"
              type="datetime-local"
              value={horaFinWall}
              onChange={(e) => setHoraFinWall(e.target.value)}
            />
          </div>
        </div>

        {minutosResultantes !== null && hayCambio ? (
          <p className="text-xs text-fg-secondary">
            Quedaría en <strong>{fmtMinutosParo(minutosResultantes)}</strong>
          </p>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sup-corr-razon">Razón del cambio (mínimo 10 caracteres)</Label>
          <Textarea
            id="sup-corr-razon"
            value={razon}
            onChange={(e) => setRazon(e.target.value)}
            placeholder="Ej: el líder registró la causal madre equivocada, fue falla mecánica"
          />
          <p className={`text-xs ${razon.trim().length >= 10 ? 'text-ok' : 'text-fg-tertiary'}`}>
            {razon.trim().length}/10 caracteres mínimos
          </p>
        </div>

        <p className="text-[11px] text-fg-tertiary">
          El cambio se aplica de inmediato y queda auditado. Si el paro estaba confirmado, la
          confirmación se cae y hay que volver a confirmarlo.
        </p>

        {error ? <p className="text-xs text-crit">{error}</p> : null}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          disabled={!hayCambio || razon.trim().length < 10 || corregir.isPending}
          onClick={() => void handleAplicar()}
        >
          {corregir.isPending ? 'Aplicando…' : 'Aplicar cambio'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
